import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useLayoutEffect,
  useRef,
} from 'react'
import { Tab, Nav, Row, Col } from 'react-bootstrap'
import objectPath from 'object-path'
import { JsonForms } from '@jsonforms/react'
import { materialCells } from '@jsonforms/material-renderers'
import { DesignContext } from 'contexts/DesignContext'
import SystemWrapper from 'designer/component/system/layout/control/ComponentWrapper'
import IconDisplay from 'designer/menu/config/menu/components/IconDisplay'
import Title from '../title/Title'
import { getShowUiConfig } from 'designer/component/action/Config'
import useAuth from 'hooks/useAuth'

import {
  checkHasShow,
  checkIsShow,
  onCheckCondition,
} from 'designer/component/action/condition/Action'
import {
  hasRules,
  onCheckRules,
  onUpdateRules,
} from 'designer/component/rules/Action'
import {
  createTabPaneUi,
  getTabElementKey,
  resolveTabUiElement,
  shouldKeepTabRender,
} from './TabHelper'

const getUiRenderKey = (elements = []) =>
  elements
    .map((element) =>
      [element?.key, getUiRenderKey(element?.elements || [])]
        .filter(Boolean)
        .join(':')
    )
    .join('|')

const TAB_ACTION_DELAY_MS = 150

const checkSelectRowRules = (designContext = {}, selectRowRulesData = {}) => {
  let { jsonForm } = designContext
  let { formData = {}, rowData = {}, ruleUiKeys = [] } = selectRowRulesData
  if (!hasRules(jsonForm)) return
  if (ruleUiKeys.length === 0) return

  objectPath.set(jsonForm, 'formData', formData)
  onCheckRules(designContext, formData, null, {
    isSelectRow: true,
    rowData,
    isDebounce: false,
    ruleUiKeys,
  })
}

const runSelectRowRules = (
  designContext = {},
  selectRowRulesData = {},
) => {
  checkSelectRowRules(designContext, selectRowRulesData)
}

const addTabRuleKey = (keys = [], key = '') => {
  if (!key) return keys
  if (keys.includes(key)) return keys

  keys.push(key)
  return keys
}

const getTabRuleKeys = (tabPanelUi = {}, tabPanelKey = '') => {
  let keys = []

  addTabRuleKey(keys, tabPanelKey)
  addTabRuleKey(keys, tabPanelUi.key)
  addTabRuleKey(keys, tabPanelUi.name)

  return keys
}

const TabPaneComponent = (props) => {
  let {
    uischema,
    schema,
    data,
    renderers,
    designContext,
    tabPaneUi,
    tabPaneKey,
    isLoadTab,
    updateData,
    isActive = true,
    isTabReady = isActive,
    isKeepTabRender = false,
    selectRowRulesData,
  } = props
  const designContextRef = useRef(designContext)
  if (isActive || !isKeepTabRender) {
    designContextRef.current = designContext
  }
  const paneDesignContext =
    isKeepTabRender && !isActive
      ? { ...designContextRef.current, openModal: null }
      : designContext
  let value = tabPaneUi
  useLayoutEffect(() => {
    if (!value) return
    if (!isActive) return
    if (!selectRowRulesData) return

    runSelectRowRules(designContext, selectRowRulesData)
  }, [isActive, selectRowRulesData])

  useEffect(() => {
    if (!value) return
    if (!isActive) return
    if (!selectRowRulesData) return

    let timerList = [50, 150].map((delayTime) =>
      setTimeout(() => {
        runSelectRowRules(designContext, selectRowRulesData)
      }, delayTime),
    )

    let cleanupTimer = setTimeout(() => {
      let { isUiChanged, setIsUiChanged } = designContext
      objectPath.del(isUiChanged, [uischema.key, 'selectRowRulesData'])
      setIsUiChanged({ ...isUiChanged })
    }, 200)

    return () => {
      timerList.forEach((timer) => clearTimeout(timer))
      clearTimeout(cleanupTimer)
    }
  }, [isActive, selectRowRulesData])

  if (!value) return <></>
  let paneRenderKey = uischema.isSameUi ? uischema.key : value.key
  let uiRenderKey = [paneRenderKey, getUiRenderKey(value.elements || [])]
    .filter(Boolean)
    .join(':')
  return (
    <Tab.Pane
      id={'tab-pane-' + paneRenderKey}
      key={'tab-pane-' + uischema.key + '-' + paneRenderKey}
      eventKey={tabPaneKey || value.key}
      style={isKeepTabRender && !isActive ? { display: 'none' } : undefined}
    >
      <DesignContext.Provider value={paneDesignContext}>
        <SystemWrapper {...props} isLoad={isLoadTab}>
          <JsonForms
            key={uiRenderKey}
            schema={schema}
            uischema={value}
            config={{
              updateData: updateData,
              isActiveTab: isTabReady,
              tabContainerKey: uischema.key,
              tabPaneKey: tabPaneKey || value.key,
            }}
            data={data}
            renderers={renderers}
            cells={materialCells}
          />
        </SystemWrapper>
      </DesignContext.Provider>
    </Tab.Pane>
  )
}

const TabPane = React.memo(
  TabPaneComponent,
  (prev, next) =>
    prev.uischema === next.uischema &&
    prev.schema === next.schema &&
    prev.data === next.data &&
    prev.renderers === next.renderers &&
    prev.tabPaneUi === next.tabPaneUi &&
    prev.tabPaneKey === next.tabPaneKey &&
    prev.isLoadTab === next.isLoadTab &&
    prev.isActive === next.isActive &&
    prev.isTabReady === next.isTabReady &&
    prev.isKeepTabRender === next.isKeepTabRender &&
    prev.selectRowRulesData === next.selectRowRulesData &&
    (!prev.isActive || prev.designContext === next.designContext),
)

const CustomTab = (props) => {
  let { uischema, data, handleChange, path, activeTabKey } = props
  let { param = [] } = uischema
  const { config } = useAuth()
  const designContext = useContext(DesignContext)
  const {
    getCurrentUi,
    isUiChanged,
    setTabActiveState,
    jsonForm,
    newUi,
  } = designContext

  const tabWidth = uischema?.tabWidth ?? 120
  const calTabWidth = tabWidth + 30
  const tabUiKeyList = uischema.elements.map(({ elements = [] }) =>
    getUiRenderKey(elements)
  )
  const hasSharedTabUi = tabUiKeyList.some(
    (uiKey, index) => uiKey && tabUiKeyList.indexOf(uiKey) !== index
  )
  const isKeepTabRender = shouldKeepTabRender(uischema, hasSharedTabUi)
  const activeName = objectPath.get(isUiChanged, ['activeTab', uischema.key])
  const firstActiveKey =
    uischema.elements.length > 0 ? getTabElementKey(uischema.elements[0]) : ''
  const defaultActiveKey = firstActiveKey
  const [loadedTabKeys, setLoadedTabKeys] = useState(() =>
    defaultActiveKey ? [defaultActiveKey] : []
  )
  const [pendingTabKey, setPendingTabKey] = useState('')
  const tabActionTimerRef = useRef()
  const activeTabClickRef = useRef()
  const defaultActiveKeyRef = useRef()

  const onUpdateTabRules = (tabKey, tabPanelUi = {}, status = {}) => {
    if (!hasRules(jsonForm)) return

    onUpdateRules(props, designContext, tabKey, jsonForm.formData, {
      isDebounce: false,
      ruleUiKeys: getTabRuleKeys(tabPanelUi, tabKey),
      ...status,
    })
  }

  const clearTabActionTimer = () => {
    if (!tabActionTimerRef.current) return
    clearTimeout(tabActionTimerRef.current)
    tabActionTimerRef.current = null
  }

  const onUpdateTabAction = (tabKey, tabPanelUi = {}) => {
    clearTabActionTimer()
    tabActionTimerRef.current = setTimeout(() => {
      tabActionTimerRef.current = null
      try {
        onUpdateTabRules(tabKey, tabPanelUi, {
          isOnClick: true,
          eventKey: 'onClick',
          isActiveTab: tabPanelUi.key || tabKey,
        })
        if (tabPanelUi.key) {
          onCheckCondition(designContext, null, tabPanelUi.key, {
            isDebounce: false,
            isOnClick: false,
            isActiveTab: tabPanelUi.key,
          })
        }
      } finally {
        setPendingTabKey('')
        setTimeout(() => {
          if (activeTabClickRef.current === tabKey) {
            activeTabClickRef.current = null
          }
        }, 250)
      }
    }, TAB_ACTION_DELAY_MS)
  }

  const updateData = (newValue) => {
    let newData = { ...data, ...newValue }
    handleChange(path, newData)
  }

  const setLoadedTab = (key) => {
    if (!isKeepTabRender || !key) return
    setLoadedTabKeys((prev) => {
      if (prev.includes(key)) return prev
      return [...prev, key]
    })
  }

  const getTabUi = (key, element) => {
    if (uischema.isSameUi) {
      return createTabPaneUi(objectPath.get(uischema.elements, [0]), 0)
    }
    let fallbackIndex = uischema.elements.findIndex(
      (value, index) => getTabElementKey(value, index) === key
    )
    let fallbackElement = objectPath.get(uischema.elements, [fallbackIndex])
    if (!element) {
      element = getCurrentUi(key)
    }
    if (!element) {
      element = fallbackElement
    }
    if (!element) return element
    element = resolveTabUiElement(key, element, fallbackElement, fallbackIndex)
    element = createTabPaneUi(element, fallbackIndex)
    return element
  }

  const setTab = (key, element, options = {}) => {
    if (!key) return
    let tabPanelUi = element || getCurrentUi(key)
    if (!tabPanelUi) {
      tabPanelUi = uischema.elements.find(
        (value, index) => getTabElementKey(value, index) === key
      )
    }
    if (!tabPanelUi) return
    if (!tabPanelUi.name) return
    let filterParam = param.filter(({ tabKey }) => tabKey === key)
    setTabActiveState({
      tabKey: uischema.key,
      tabPanelName: tabPanelUi.name,
      filterParam,
      shouldSetParam: param.length > 0,
      clearTabState: options.clearTabState === true,
    })
    setLoadedTab(key)
    return tabPanelUi
  }

  let { tabKey, tabPanelUi, selectRowRulesData } =
    objectPath.get(isUiChanged, [uischema.key]) || {}
  useEffect(() => {
    if (tabKey) {
      let nextTabKey = tabKey
      let nextTabPanelUi = tabPanelUi
      let isSameActiveTab = activeTabKey === nextTabKey
      let isClickRedirect =
        activeTabClickRef.current && activeTabClickRef.current !== nextTabKey
      if (isClickRedirect || isSameActiveTab) {
        setTabActiveState({
          tabKey: uischema.key,
          clearTabState: true,
        })
        return
      }
      setPendingTabKey(nextTabKey)
      setTab(nextTabKey, nextTabPanelUi, { clearTabState: true })
      clearTabActionTimer()
      tabActionTimerRef.current = setTimeout(() => {
        tabActionTimerRef.current = null
        onCheckCondition(designContext, null, nextTabKey, {
          isDebounce: false,
          isOnClick: false,
        })
        setPendingTabKey('')
      }, TAB_ACTION_DELAY_MS)
    }
  }, [tabKey])

  useEffect(() => {
    return () => {
      clearTabActionTimer()
    }
  }, [])

  useEffect(() => {
    if (tabKey) return
    if (!defaultActiveKey) return
    if (defaultActiveKeyRef.current) return
    defaultActiveKeyRef.current = defaultActiveKey
    if (!activeName) {
      setTab(defaultActiveKey)
    }
    let defaultActiveUi = getTabUi(defaultActiveKey)
    if (defaultActiveUi?.key) {
      onCheckCondition(designContext, null, defaultActiveUi.key, {
        isDebounce: false,
        isOnClick: false,
      })
    }
  }, [defaultActiveKey])

  useEffect(() => {
    setLoadedTab(activeTabKey)
  }, [activeTabKey, isKeepTabRender])

  const tabPaneUi = useMemo(() => {
    return getTabUi(activeTabKey)
  }, [uischema.elements, activeTabKey, newUi])
  const activeTabUi = uischema.elements.find(
    (value, index) => getTabElementKey(value, index) === activeTabKey,
  )

  const keepTabPaneList = useMemo(() => {
    return uischema.elements
      .filter((value, index) =>
        loadedTabKeys.includes(getTabElementKey(value, index))
      )
      .map((value, index) =>
        getTabUi(getTabElementKey(value, index), value)
      )
  }, [JSON.stringify(loadedTabKeys), activeTabKey, uischema.elements, newUi])

  return (
    <div
      style={{
        padding: uischema.layoutPadding ?? '.5rem',
        margin: uischema.layoutMargin,
        minWidth: uischema.minWidth,
        maxWidth: uischema.maxWidth,
      }}
    >
      <Row className="w-100 mx-0">
        <Col
          sm={uischema.navLayout ? 'auto' : 12}
          style={{
            minWidth: tabWidth + 'px',
            flexWrap: uischema.navLayout ? 'nowrap' : 'wrap',
          }}
          className={uischema.navLayout ? '' : 'mx-2'}
        >
          <Nav
            variant="pills"
            activeKey={activeTabKey}
            className={uischema.navLayout}
            style={{ zIndex: uischema?.zIndex ?? 0 }}
          >
            {uischema.elements.map((value, index) => {
              let tabKey = getTabElementKey(value, index)
              let hasShow = checkHasShow({ designContext, uischema: value })
              if (hasShow) {
                let isShow = checkIsShow({ designContext, uischema: value })
                if (!isShow) return
              }
              let isShowUi = getShowUiConfig({
                uischema: value,
                config,
                jsonForm,
              })
              if (!isShowUi) return
              let textColor = value?.textColor ?? '#4099ff'
              let tabColor = value?.tabColor ?? 'transparent'
              let inActiveStyle = {
                backgroundColor: tabColor,
                color: '#000',
                border: `2px solid ${tabColor}`,
                borderRadius: '6px 6px 0px 0px',
                borderBottomColor: tabColor,
                margin: '0 .5px',
              }
              let activeStyle = {
                backgroundColor: 'transparent',
                color: textColor,
                border: `2px solid transparent`,
                borderBottomColor: textColor,
                borderRadius: '6px 6px 0px 0px',
                margin: '0 .5px',
              }
              return (
                <Nav.Item
                  id={'tab-nav-' + tabKey}
                  key={'tab-nav-' + tabKey}
                >
                  <Nav.Link
                    style={
                      activeTabKey === tabKey ? activeStyle : inActiveStyle
                    }
                    eventKey={tabKey}
                    className="d-flex"
                    onClick={(event) => {
                      event.preventDefault()
                      if (activeTabKey === tabKey) return
                      activeTabClickRef.current = tabKey
                      setPendingTabKey(tabKey)
                      let tabPanelUi = setTab(tabKey, value)
                      if (tabPanelUi) {
                        onUpdateTabAction(tabKey, tabPanelUi)
                      }
                    }}
                  >
                    <IconDisplay menu={value} />
                    <Title
                      {...props}
                      uischema={value}
                      label={value.title}
                      isActive={activeTabKey === tabKey}
                      activeColor={tabColor}
                      activeTextColor={'#FFF'}
                    />
                  </Nav.Link>
                </Nav.Item>
              )
            })}
          </Nav>
        </Col>
        <Col
          sm={uischema.navLayout ? '' : 12}
          style={{
            minWidth: `calc(100% - ${calTabWidth}px)`,
          }}
          className="p-0"
        >
          {isKeepTabRender ? (
            keepTabPaneList.map((value) => (
              <TabPane
                {...props}
                key={'keep-tab-pane-' + value.key}
                designContext={designContext}
                updateData={updateData}
                tabPaneUi={value}
                isActive={activeTabKey === value.key}
                isTabReady={
                  activeTabKey === value.key &&
                  activeName === value.name &&
                  pendingTabKey !== value.key
                }
                isKeepTabRender={isKeepTabRender}
                selectRowRulesData={
                  selectRowRulesData?.tabKey === value.key
                    ? selectRowRulesData
                    : null
                }
              />
            ))
          ) : (
            <TabPane
              {...props}
              designContext={designContext}
              updateData={updateData}
              tabPaneUi={tabPaneUi}
              tabPaneKey={uischema.isSameUi ? firstActiveKey : activeTabKey}
              isActive={true}
              isTabReady={
                uischema.isSameUi || activeName === activeTabUi?.name
              }
              selectRowRulesData={
                selectRowRulesData?.tabKey === activeTabKey
                  ? selectRowRulesData
                  : null
              }
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

export default CustomTab
